import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { extractApiErrorMessage } from '@/lib/api-client';
import { useVoidInvoiceMutation } from '../api/use-void-invoice-mutation';
import { invoiceActionAvailable } from '../lib/invoice-actions';
import { invoiceTypeLabel } from '../lib/invoice-label';
import type { InvoiceRow } from '../types';

const MIN_REASON_LENGTH = 5;

export interface VoidInvoiceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly onVoided?: (() => void) | undefined;
}

interface VoidFormProps {
  readonly reason: string;
  readonly setReason: (value: string) => void;
  readonly canSubmit: boolean;
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}

function VoidForm({ reason, setReason, canSubmit, isPending, onCancel, onSubmit }: VoidFormProps) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
      className="space-y-3"
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="void_reason">Reason</Label>
        <Textarea
          id="void_reason"
          required
          minLength={MIN_REASON_LENGTH}
          maxLength={2000}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          disabled={isPending}
          rows={4}
          data-testid="void-invoice-reason"
        />
        <p className="text-muted-foreground text-xs">At least {MIN_REASON_LENGTH} characters.</p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="destructive"
          disabled={!canSubmit}
          data-testid="void-invoice-confirm"
        >
          {isPending ? 'Voiding…' : 'Void'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function VoidInvoiceDialog(props: VoidInvoiceDialogProps) {
  const { open, onOpenChange, invoice, onVoided } = props;
  const label = invoiceTypeLabel(invoice);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const mutation = useVoidInvoiceMutation({
    invoiceId: invoice.id,
    orderId: invoice.order_id ?? '',
    blOrderId: invoice.bl_order_id ?? undefined,
  });
  const trimmed = reason.trim();
  const canSubmit = trimmed.length >= MIN_REASON_LENGTH && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Void this {label}?</DialogTitle>
          <DialogDescription>
            The row stays visible in listings but is excluded from outstanding math, payment
            recording, and any future Adjustment Note. Provide a reason for the audit trail.
          </DialogDescription>
        </DialogHeader>
        <VoidForm
          reason={reason}
          setReason={setReason}
          canSubmit={canSubmit}
          isPending={mutation.isPending}
          onCancel={() => {
            onOpenChange(false);
          }}
          onSubmit={() => {
            mutation.mutate(trimmed, {
              onSuccess: () => {
                toast.success(`${label} voided.`);
                onVoided?.();
                onOpenChange(false);
              },
              onError: (err) => {
                toast.error(extractApiErrorMessage(err, `Failed to void the ${label}.`));
              },
            });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export interface VoidButtonProps {
  readonly invoice: InvoiceRow;
  readonly onVoided?: (() => void) | undefined;
  readonly compact?: boolean | undefined;
}

export function VoidButton(props: VoidButtonProps) {
  const { invoice, onVoided, compact } = props;
  const [open, setOpen] = useState(false);
  if (!invoiceActionAvailable(invoice, 'void')) return null;
  return (
    <>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        className={compact ? 'h-7 gap-1 px-3 text-xs' : undefined}
        data-testid={`invoice-${invoice.id}-void`}
        onClick={() => {
          setOpen(true);
        }}
      >
        Void
      </Button>
      <VoidInvoiceDialog open={open} onOpenChange={setOpen} invoice={invoice} onVoided={onVoided} />
    </>
  );
}
